import { createContext, useContext, useRef, useState, useCallback, type ReactNode } from "react";
import type { DocTypeSchema } from "@/lib/erp/meta";
import type { RecordSummary } from "@/lib/erp/recordSummary";

/**
 * API that a detail page exposes to the dev autofill system
 */
export interface ActiveDetailPageApi {
  /** DocType name (e.g., "Item", "Sales Order") */
  doctype: string;
  /** Get current meta schema (may be null if not loaded yet) */
  getMeta: () => DocTypeSchema | null;
  /** Get current form data */
  getFormData: () => Record<string, any>;
  /** Apply a patch to form data (batched update) */
  applyPatch: (patch: Record<string, any>) => void;
  /** Apply new rows to a table field */
  applyTablePatch: (fieldname: string, nextRows: any[]) => void;
  /** Whether meta is loaded and ready */
  isReady: boolean;
  /** Name of the SAVED document on screen (null for a new, unsaved document) */
  docName?: string | null;
  /** Human title of the saved document (meta.title_field, else name) */
  getTitle?: () => string | null;
  /** Saved-values summary: docstatus/status + a few key fields from meta (never unsaved edits) */
  getRecordSummary?: () => RecordSummary | null;
  /** Active tab of THIS page (read from its nearest DetailPageTabsProvider) */
  getActiveTab?: () => { id: string; label?: string } | null;
}

/**
 * Context value for active detail page registration
 */
interface ActiveDetailPageContextValue {
  /** Register a detail page as active */
  register: (api: ActiveDetailPageApi, id: string) => void;
  /** Clear active page registration */
  clear: (id: string) => void;
  /** Get current active page API (or null) */
  getActive: () => ActiveDetailPageApi | null;
  /** Version counter that increments on register/clear (for UI reactivity) */
  activeVersion: number;
  /**
   * Tell listeners the active page's title/tab/status changed (no React re-render).
   * Ignored unless `id` is the active registration.
   */
  notifyChange: (id: string) => void;
  /** Listen for register/clear/notifyChange without re-rendering; returns unsubscribe */
  subscribe: (listener: () => void) => () => void;
}

const ActiveDetailPageContext = createContext<ActiveDetailPageContextValue | null>(null);

/**
 * Provider for active detail page registration
 * 
 * Allows global dev tools (like DevModeToggle) to access the currently
 * mounted detail page's meta + form state without hardcoded route detection.
 */
export function ActiveDetailPageProvider({ children }: { children: ReactNode }) {
  // Store active API in ref (doesn't cause rerenders)
  const activeRef = useRef<ActiveDetailPageApi | null>(null);
  const activeIdRef = useRef<string | null>(null);
  
  // Version counter for UI reactivity (increments on register/clear)
  const [activeVersion, setActiveVersion] = useState(0);

  // Non-rendering listeners (page-context reporter) — refs, so no provider re-render
  const listenersRef = useRef(new Set<() => void>());
  const emit = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      try {
        listener();
      } catch {
        // a listener must never break registration
      }
    });
  }, []);

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const notifyChange = useCallback(
    (id: string) => {
      if (activeIdRef.current === id) emit();
    },
    [emit]
  );

  const register = useCallback((api: ActiveDetailPageApi, id: string) => {
    activeRef.current = api;
    activeIdRef.current = id;
    setActiveVersion((v) => v + 1); // Trigger UI update
    emit();
    if (import.meta.env.DEV) {
      console.log(`[ActiveDetailPage] Registered: ${api.doctype} (id: ${id})`);
    }
  }, [emit]);

  const clear = useCallback((id: string) => {
    // Only clear if this is the currently registered page (prevent stale clears)
    if (activeIdRef.current === id) {
      activeRef.current = null;
      activeIdRef.current = null;
      setActiveVersion((v) => v + 1); // Trigger UI update
      emit();
      if (import.meta.env.DEV) {
        console.log(`[ActiveDetailPage] Cleared: ${id}`);
      }
    }
  }, [emit]);

  const getActive = useCallback(() => {
    return activeRef.current;
  }, []);

  return (
    <ActiveDetailPageContext.Provider
      value={{
        register,
        clear,
        getActive,
        activeVersion,
        notifyChange,
        subscribe,
      }}
    >
      {children}
    </ActiveDetailPageContext.Provider>
  );
}

/**
 * Hook to access active detail page registration API
 */
export function useActiveDetailPageContext() {
  const context = useContext(ActiveDetailPageContext);
  if (!context) {
    throw new Error("useActiveDetailPageContext must be used within ActiveDetailPageProvider");
  }
  return context;
}

/** Same as useActiveDetailPageContext but returns null outside the provider (e.g. isolated tests). */
export function useOptionalActiveDetailPageContext() {
  return useContext(ActiveDetailPageContext);
}

/**
 * Hook to get current active page API (for consumers like DevModeToggle)
 * Returns null if no page is registered
 */
export function useActiveDetailPage() {
  const { getActive, activeVersion } = useActiveDetailPageContext();
  // Return API + version (version changes trigger rerender)
  return {
    api: getActive(),
    activeVersion, // Include in return so consumers rerender when it changes
  };
}
